import { isNil, isNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridCellProps, MultiGrid } from 'react-virtualized';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import {
  assertCondition,
  assertNotNil,
  byTypeName,
} from '../../../utils/types';
import {
  exportTableToCsv,
  MultiGridTable,
  useTableColumnMapping,
  useTableColumnSortState,
  useTableColumnWidthState,
  useTableSearchState,
} from '../../MultiGridTable';
import * as ChartAnalysisTableUI from './ChartAnalysisTableUI';
import {
  defaultSort,
  generateBaseHeaders,
  generateBaseRows,
  generateDefaultTableColumnOrderMapping,
  generateFilteredRows,
  getFirstNonOverallSlice,
  groupCellSort,
} from './tableUtils';
import {
  attemptFormatDate,
  formatValueWithMeasureFormat,
} from '../../../utils/formatting';
import { formatNumberWithComma } from '../../../utils/formatText';
import { setChartSettingsTableSettingsAction } from '../../../redux/slices/analyses';
import { useAppDispatch } from '../../../redux/hooks';
import { useCurrentUrl } from '../../../utils/routing';
import ChartAnalysisTableSort from './ChartAnalysisTableSort';
import { SecondaryButton } from '../../Button';
import * as ChartGroupsUI from '../ChartLegend/ChartGroups/ChartGroupsUI';
import useDashboardSelector from '../../../redux/selectorHooks/useDashboardSelector';
import ChartTopBreakdownSelector from '../ChartTopBreakdownSelector';
import TickCheckbox from '../../Checkbox/TickCheckbox';
import { DragStartEvent } from '@dnd-kit/core';
import ChartHeaderDragOverlay from './ChartHeaderDragOverlay';
import DraggableHeaderCell from './DraggableHeaderCell';
import { TableSortType } from '../interfaces';
import { SvgIcon } from '../../Icon';
import DndSortable from '../../DndSortable';
import {
  ColumnFilterConfig,
  ColumnSortConfig,
} from '../../MultiGridTable/ColumnFilterWidget/types';
import { ChartMode } from '../types';
import useToggleSlice from '../hooks/useToggleSlice';
import { theme } from '../../../styles/theme';
import KubitTooltip from '../../KubitTooltip';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { buttonClicked } from '../../../utils/analytics/events/button/events';
import BadgeRound from '../../BadgeRound';
import { useChartContext } from '../ChartContext';
import {
  ChartViewOptions,
  defaultTableSettings,
} from '../../../redux/utils/chartSettings';
import { IS_SCREENSHOT_MODE } from '../../../config/constants';
import styled from 'styled-components';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const TABLE_HEIGHT = 600;
const ROW_HEIGHT = 32;
const DEFAULT_CELL_WIDTH = 160;
const DASHBOARD_TABLE_HEIGHT = 320;
const RANK_CELL_WIDTH = 80;

const Container = styled.div`
  @media print {
    break-inside: avoid;
  }
`;

const ChartAnalysisTable = ({ analysisId, mode }: IProps) => {
  const dispatch = useAppDispatch();
  const multiGridRef = useRef<MultiGrid>(null);
  const {
    chartData,
    displayedMeasures,
    displayedSlices,
    colorConfig,
    settings: { chartSettings },
    metadata: { display, comparisonInfo },
  } = useChartContext();
  const { tableSettings, expressionSettings } = chartSettings;
  const { track } = useAnalytics();
  // only available for expression
  assertCondition(chartData.queryType === QueryType.Expression);

  const hasRankCell = !isNil(getFirstNonOverallSlice(displayedSlices)?.rank);

  const { isDashboardInUrl, isWorkspacesInUrl } = useCurrentUrl();
  const isCompactMode = mode === ChartMode.Compact;
  const dashboard = useDashboardSelector('dashboard');

  const headers = useMemo(
    () => generateBaseHeaders(chartData, displayedSlices),
    [chartData, displayedSlices],
  );
  const defaultColumnWidths = Array.from({ length: headers.length }).map(
    () => DEFAULT_CELL_WIDTH,
  );

  const {
    isResizing,
    columnWidths,
    getColumnWidth,
    handleMouseDown,
    onChangeColumnWidths,
    setMultiGridWidth,
    multiGridWidth,
  } = useTableColumnWidthState({
    analysisId,
    initialColumnWidths: defaultColumnWidths,
    hasRankCell,
  });

  const { sortColumnIndex, sortDirection, sortType, onSort, updateSortIndex } =
    useTableColumnSortState(analysisId);

  const { searchTerm, onSearch } = useTableSearchState(analysisId);

  const [draggedHeader, setDraggedHeader] = useState<string | null>(null);

  const showCompare =
    !isNil(comparisonInfo) &&
    !comparisonInfo.isEmpty &&
    !isNil(expressionSettings) &&
    expressionSettings.showCompare;
  const showMeasure =
    !isNil(expressionSettings) && expressionSettings.showMeasure;

  const defaultRows = generateBaseRows({
    chartData,
    displayedMeasures,
    displayedSlices,
    colorConfig,
    showCompare,
    showMeasure,
  });

  const defaultColumnMapping = useMemo(() => {
    return generateDefaultTableColumnOrderMapping(defaultRows);
  }, [defaultRows]);

  const { columnMapping, updateColumnMapping } = useTableColumnMapping({
    analysisId,
    defaultColumnMapping,
  });

  const baseRows = useMemo(() => {
    const { filterConfig } = tableSettings;
    if (filterConfig && !isNil(filterConfig.columnIndex)) {
      const { columnIndex } = filterConfig;
      const colIndex = columnMapping.findIndex(
        (v: number) => v === columnIndex,
      );

      return generateFilteredRows({
        filterConfig,
        defaultRows,
        colIndex,
      });
    } else {
      return defaultRows;
    }
  }, [defaultRows, tableSettings, columnMapping]);

  const tableHeight = useMemo(() => {
    const numRows = baseRows.length + 1;
    const tableHeight = numRows * ROW_HEIGHT + 10;

    if (
      (isDashboardInUrl || isWorkspacesInUrl) &&
      tableHeight > DASHBOARD_TABLE_HEIGHT
    ) {
      return DASHBOARD_TABLE_HEIGHT;
    }

    if (tableHeight > TABLE_HEIGHT) {
      return TABLE_HEIGHT;
    }
    return tableHeight;
  }, [baseRows, isDashboardInUrl, isWorkspacesInUrl]);

  // fix columns if there are any fixed cells in the first row
  const fixedColumnCount = useMemo(() => {
    if (isWorkspacesInUrl) {
      return 0;
    }

    if (isDashboardInUrl && dashboard) {
      const dashboardRow = dashboard.rows.find((row) =>
        row.columns
          .filter(byTypeName('DashboardAnalysisColumn'))
          .find(({ analysis }) => analysis.id === analysisId),
      );
      const numColumns = dashboardRow?.columns?.length ?? 0;

      // KB-7207, do not fix the columns if there are more than 1 column in a dashboard row
      if (numColumns > 1) {
        return 0;
      }
    }

    const numFixedColumns =
      baseRows[0]?.cells?.filter((cell) => cell.isFixedCell)?.length ?? 0;

    // KB-7665 if the aggregate width of the fixed columns is greater than 50% of the multigrid width
    // do not fix any columns and allow horizontal scroll across all columns.
    if (numFixedColumns > 0 && !isNil(multiGridWidth)) {
      const fixedColumnWidths = columnWidths
        .slice(0, numFixedColumns)
        .reduce((a: number, b: number) => a + b, 0);
      if (fixedColumnWidths > multiGridWidth / 2) {
        return 0;
      }
    }

    return numFixedColumns;
  }, [
    baseRows,
    isDashboardInUrl,
    isWorkspacesInUrl,
    dashboard,
    analysisId,
    columnWidths,
    multiGridWidth,
  ]);

  const toggleSlice = useToggleSlice(analysisId);

  const filteredAndSortedData = useMemo(() => {
    // Filter data based on searchTerm.
    let result = [...baseRows];

    if (searchTerm?.length) {
      result = result.filter((row) =>
        // Check if any cell in the row includes the searchTerm
        row.cells.some((cell) => {
          const stringValue = cell.value.toString();
          return (
            stringValue.length &&
            stringValue.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }),
      );
    }

    // Sort the filtered data based on sortColumnIndex and sortDirection
    if (!isNil(sortColumnIndex)) {
      const sortIndex = columnMapping[sortColumnIndex];
      result.sort((a, b) => {
        // Replace commas in number strings to avoid unexpected parseFloat results
        const aValue = a.cells[sortIndex].value;
        const bValue = b.cells[sortIndex].value;

        // different sorting strategy for group cell
        if (a.cells[sortIndex].isGroupCell) {
          return groupCellSort({
            sortType,
            sortDirection,
            aValue,
            bValue,
          });
        }

        return defaultSort({
          sortDirection,
          aValue,
          bValue,
        });
      });
    }

    // Return the filtered and sorted data for rendering
    return result;
  }, [
    sortColumnIndex,
    sortDirection,
    searchTerm,
    sortType,
    baseRows,
    columnMapping,
  ]);

  const handleSortColumn = useCallback(
    (columnIndex: number, sortType?: TableSortType) => {
      onSort({
        index: columnMapping[columnIndex],
        sortType,
      });
      updateSortIndex(columnIndex);
    },
    [columnMapping, onSort, updateSortIndex],
  );

  // gets the total value of numbers in a visible column
  const getTotalColumnValue = useCallback(
    (columnIndex: number) => {
      const mappedIndex = columnMapping[columnIndex];
      return filteredAndSortedData.reduce((acc, curr) => {
        const cell = curr.cells[mappedIndex];
        if (cell && !cell.isDisabled && isNumber(cell.value)) {
          acc += cell.value;
        }
        return acc;
      }, 0);
    },
    [columnMapping, filteredAndSortedData],
  );

  const onChangeFilter = useCallback(
    (index: number, filterConfig: ColumnFilterConfig | null) => {
      dispatch(
        setChartSettingsTableSettingsAction({
          analysisId,
          tableSettings: {
            filterConfig: filterConfig
              ? {
                  columnIndex: index,
                  ...filterConfig,
                }
              : null,
          },
        }),
      );
    },
    [analysisId, dispatch],
  );
  const onChangeSort = useCallback(
    (index: number, sortConfig: ColumnSortConfig | null) => {
      dispatch(
        setChartSettingsTableSettingsAction({
          analysisId,
          tableSettings: {
            sortConfig: sortConfig
              ? {
                  ...sortConfig,
                  index,
                }
              : null,
          },
        }),
      );
      onSort({
        index,
        sortOrder: sortConfig?.sortOrder ?? undefined,
        sortType: sortConfig?.sortType ?? undefined,
      });
    },
    [onSort, analysisId, dispatch],
  );

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }: GridCellProps) => {
      // Use mapped column index when retrieving data
      // and columnIndex when passing in grid column properties
      const colIndex = columnMapping[columnIndex] ?? columnIndex;

      if (rowIndex === 0) {
        const numFixedColumns =
          baseRows[0]?.cells?.filter((cell) => cell.isFixedCell)?.length ?? 0;
        const showDraggableHeaderCell = hasRankCell
          ? colIndex > 0 && colIndex < numFixedColumns
          : colIndex < numFixedColumns;

        if (showDraggableHeaderCell) {
          const columnValues = [
            ...new Set(defaultRows.map((row) => row.cells[colIndex].value)),
          ];
          const filterConfig =
            !isNil(tableSettings.filterConfig?.columnIndex) &&
            tableSettings.filterConfig?.columnIndex === columnIndex
              ? tableSettings.filterConfig
              : null;
          return (
            <DraggableHeaderCell
              key={key}
              id={headers[colIndex]}
              style={style}
              columnIndex={columnIndex}
              isOver={
                draggedHeader
                  ? headers[columnMapping[columnIndex]] === draggedHeader
                  : false
              }
              onChangeFilter={onChangeFilter}
              onChangeSort={onChangeSort}
              filterConfig={filterConfig}
              sortConfig={tableSettings.sortConfig}
              columnValues={columnValues}
              isResizing={isResizing}
            >
              <ChartAnalysisTableSort
                display={headers[colIndex]}
                columnIndex={columnIndex}
                sortColumnIndex={sortColumnIndex}
                sortDirection={sortDirection}
                onSort={handleSortColumn}
              />
              {rowIndex === 0 && (
                <ChartAnalysisTableUI.ChartColumnResizeHandle
                  onMouseDown={(e) => handleMouseDown(e, columnIndex)}
                />
              )}
            </DraggableHeaderCell>
          );
        }

        return (
          <ChartAnalysisTableUI.HeaderCell
            key={key}
            style={style}
            $rowIndex={rowIndex}
            $columnIndex={columnIndex}
          >
            <ChartAnalysisTableSort
              display={attemptFormatDate(headers[colIndex])}
              columnIndex={columnIndex}
              sortColumnIndex={sortColumnIndex}
              sortDirection={sortDirection}
              onSort={handleSortColumn}
            />

            {rowIndex === 0 && (
              <ChartAnalysisTableUI.ChartColumnResizeHandle
                onMouseDown={(e) => handleMouseDown(e, columnIndex)}
              />
            )}
          </ChartAnalysisTableUI.HeaderCell>
        );
      }

      const cellData = filteredAndSortedData[rowIndex - 1].cells[colIndex];
      if (cellData) {
        const displayValue =
          typeof cellData.value === 'number' && cellData.format
            ? formatValueWithMeasureFormat(cellData.value, cellData.format)
            : formatNumberWithComma(cellData.value);
        const totalColumnValue = getTotalColumnValue(columnIndex);
        const columnWidth = getColumnWidth({ index: columnIndex });
        const barWidth = isNumber(cellData.value)
          ? (cellData.value / totalColumnValue) * columnWidth
          : null;
        const shouldShowHorizontalBars =
          tableSettings.showHorizontalBars &&
          !cellData.isDisabled &&
          cellData.isSliceVisible &&
          isNumber(cellData.value);

        return (
          <ChartAnalysisTableUI.Cell
            key={key}
            style={style}
            title={
              typeof cellData.value === 'string' ? cellData.value : undefined
            }
            $rowIndex={rowIndex}
            $columnIndex={columnIndex}
            $isDisabled={cellData.isDisabled || !cellData.isSliceVisible}
            $backgroundColor={
              rowIndex % 2 === 0 && !cellData.isFixedCell
                ? theme.colors.level1
                : cellData.backgroundColor
            }
            $color={cellData.color}
            $isFixedCell={cellData.isFixedCell}
          >
            {cellData.isMeasureCell ? (
              <ChartAnalysisTableUI.BaseCell>
                {cellData.measureLabel && (
                  <ChartAnalysisTableUI.ValueCell>
                    <BadgeRound
                      bgColor={
                        cellData.measureColor ?? theme.colors.secondary800
                      }
                      size="small"
                    >
                      {cellData.measureLabel}
                    </BadgeRound>
                  </ChartAnalysisTableUI.ValueCell>
                )}
                <ChartAnalysisTableUI.ValueCell>
                  {cellData.value}
                </ChartAnalysisTableUI.ValueCell>
              </ChartAnalysisTableUI.BaseCell>
            ) : cellData.isRankCell ? (
              <ChartAnalysisTableUI.BaseCell>
                <TickCheckbox
                  checked={!cellData.isDisabled && cellData.isSliceVisible}
                  disabled={cellData.isDisabled}
                  onClick={() => {
                    if (cellData.isDisabled) {
                      return;
                    }
                    assertNotNil(cellData.sliceKey);
                    assertNotNil(cellData.isSliceVisible);
                    toggleSlice(cellData.sliceKey, cellData.isSliceVisible);
                  }}
                />
                {cellData.value}
              </ChartAnalysisTableUI.BaseCell>
            ) : cellData.sliceColor && cellData.isGroupCell ? (
              <ChartAnalysisTableUI.BaseCell>
                <ChartGroupsUI.GroupItemIcon
                  $color={cellData.sliceColor}
                  $disabled={cellData.isDisabled || !cellData.isSliceVisible}
                />
                {cellData.value}
              </ChartAnalysisTableUI.BaseCell>
            ) : (
              <ChartAnalysisTableUI.ValueCell>
                {shouldShowHorizontalBars && (
                  <svg width={`${barWidth}px`} height="20">
                    <rect width="100%" height="20" fill={cellData.sliceColor} />
                  </svg>
                )}
                {displayValue}
              </ChartAnalysisTableUI.ValueCell>
            )}

            {rowIndex === 0 && (
              <ChartAnalysisTableUI.ChartColumnResizeHandle
                onMouseDown={(e) => handleMouseDown(e, columnIndex)}
              />
            )}
          </ChartAnalysisTableUI.Cell>
        );
      }
    },
    [
      columnMapping,
      filteredAndSortedData,
      baseRows,
      hasRankCell,
      headers,
      sortColumnIndex,
      sortDirection,
      handleSortColumn,
      defaultRows,
      tableSettings,
      draggedHeader,
      onChangeFilter,
      onChangeSort,
      isResizing,
      handleMouseDown,
      getTotalColumnValue,
      getColumnWidth,
      toggleSlice,
    ],
  );

  const onResize = useCallback(() => {
    if (isResizing) {
      return;
    }
    if (multiGridRef?.current) {
      const multiGridWidth = multiGridRef.current.props.width;

      const allColumnsWidth = tableSettings?.columnWidths?.length
        ? tableSettings.columnWidths.reduce((a: number, b: number) => a + b, 0)
        : 0;

      // if the sum of all the column widths is less than the width of the multigrid
      // recompute the column widths
      // or if the column widths from chart settings do not match in length with the headers length
      // recompute the column widths
      if (
        (multiGridWidth > 0 &&
          (isNil(tableSettings?.columnWidths) ||
            allColumnsWidth < multiGridWidth)) ||
        tableSettings?.columnWidths?.length !== headers.length
      ) {
        const newColWidths = Array.from({ length: headers.length }).map(
          (_, index) => {
            let cellWidth = multiGridWidth / headers.length;
            if (hasRankCell) {
              if (index === 0) {
                return RANK_CELL_WIDTH;
              }

              // if rank column exists, cell widths for non-rank columns will need
              // to consider the width of the rank cell (which is smaller than normal columns)
              cellWidth =
                (multiGridWidth - RANK_CELL_WIDTH) / (headers.length - 1);
            }

            return Math.max(DEFAULT_CELL_WIDTH, cellWidth) - 1;
          },
        );

        onChangeColumnWidths(newColWidths);
      }
    }
  }, [
    multiGridRef,
    headers,
    tableSettings,
    onChangeColumnWidths,
    hasRankCell,
    isResizing,
  ]);

  // if the columnWidths array has changed, recompute the multigrid
  useEffect(() => {
    if (multiGridRef?.current) {
      multiGridRef.current.recomputeGridSize();

      // set the multigrid width state in the useTableColumnWidthState hook
      if (multiGridRef.current.props.width !== multiGridWidth) {
        setMultiGridWidth(multiGridRef.current.props.width);
      }
    }
  }, [multiGridRef, columnWidths, multiGridWidth, setMultiGridWidth]);

  // for the case of chart settings reset
  // if the columnWidths are nil, call onResize
  // to recompute the column widths
  useEffect(() => {
    if (isNil(tableSettings?.columnWidths)) {
      onResize();
    }
  }, [tableSettings, onResize]);

  const showReset =
    searchTerm?.length > 0 ||
    !isNil(sortColumnIndex) ||
    tableSettings.filterConfig;

  const onReset = () =>
    dispatch(
      setChartSettingsTableSettingsAction({
        analysisId,
        tableSettings: {
          ...defaultTableSettings,
          chartView:
            isDashboardInUrl || isWorkspacesInUrl
              ? ChartViewOptions.Table
              : ChartViewOptions.ChartAndTable,
        },
      }),
    );

  const showHorizontalBars = () => {
    track(
      buttonClicked({ context: 'reports', action: 'chart table - show bars' }),
    );

    dispatch(
      setChartSettingsTableSettingsAction({
        analysisId,
        tableSettings: {
          showHorizontalBars: !tableSettings.showHorizontalBars,
        },
      }),
    );
  };

  const onExportToCsv = () => {
    const csvHeaders = headers.map((_, headerIndex) => {
      return headers[columnMapping[headerIndex]];
    });

    const data = filteredAndSortedData.map((row) =>
      row.cells.map((_, colIndex) => row.cells[columnMapping[colIndex]].value),
    );

    exportTableToCsv({
      display: `${display}-${analysisId}`,
      headers: csvHeaders,
      data,
    });
  };

  // disabling default browser context menu
  // in order to make our custom context menus (expand, create cohort, etc.)
  // be a single UI element
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (sourceIndex: number, destinationIndex: number) => {
    const sIndex = columnMapping.findIndex(
      (value: number) => value === sourceIndex,
    );
    const dIndex = columnMapping.findIndex(
      (value: number) => value === destinationIndex,
    );
    const newMapping = updateColumnMapping(sIndex, dIndex);

    if (!isNil(sortColumnIndex)) {
      const curIndex = columnMapping[sortColumnIndex];
      const newIndex = newMapping.findIndex((value) => value === curIndex);
      updateSortIndex(newIndex);
    }

    const { filterConfig } = tableSettings;
    if (filterConfig && !isNil(filterConfig.columnIndex)) {
      const curIndex = columnMapping[filterConfig.columnIndex];
      const newIndex = newMapping.findIndex((value) => value === curIndex);
      dispatch(
        setChartSettingsTableSettingsAction({
          analysisId,
          tableSettings: {
            filterConfig: {
              ...filterConfig,
              columnIndex: newIndex,
            },
          },
        }),
      );
    }

    setDraggedHeader(null);
  };

  const onDragStart = (event: DragStartEvent) => {
    const sortable = event.active?.data.current?.sortable;
    if (sortable) {
      setDraggedHeader(sortable.items[sortable.index]);
    }
  };

  const showHeading = !isCompactMode && !IS_SCREENSHOT_MODE;

  return (
    <>
      {showHeading && (
        <ChartAnalysisTableUI.ContainerHeader>
          <ChartTopBreakdownSelector analysisId={analysisId} />
          <ChartAnalysisTableUI.FlexRow>
            {showReset && (
              <KubitTooltip
                content={'Reset to default view'}
                placement="bottom"
              >
                <SecondaryButton
                  onClick={onReset}
                  isOutlined={true}
                  trackContext="reports"
                  trackAction="chart table - reset"
                >
                  Reset
                </SecondaryButton>
              </KubitTooltip>
            )}
            <KubitTooltip
              content={'Export the table to CSV'}
              placement="bottom"
            >
              <SecondaryButton
                onClick={onExportToCsv}
                isOutlined={true}
                trackContext="reports"
                trackAction="chart table - export csv"
              >
                Export Table to CSV
              </SecondaryButton>
            </KubitTooltip>
            <ChartAnalysisTableUI.StyledSearchInput
              value={searchTerm}
              onChange={onSearch}
            />
            <KubitTooltip
              content={
                tableSettings.showHorizontalBars
                  ? 'Hide horizontal bars'
                  : 'Show horizontal bars'
              }
              placement="bottom"
            >
              <ChartAnalysisTableUI.ShowHorizontalBarButton
                onClick={showHorizontalBars}
              >
                <SvgIcon
                  name="horizontal-bar"
                  width={12}
                  height={12}
                  isActive={tableSettings.showHorizontalBars}
                  customFill="currentColor"
                />
              </ChartAnalysisTableUI.ShowHorizontalBarButton>
            </KubitTooltip>
          </ChartAnalysisTableUI.FlexRow>
        </ChartAnalysisTableUI.ContainerHeader>
      )}

      <Container
        onContextMenu={handleContextMenu}
        style={{ height: tableHeight }}
      >
        <DndSortable
          onDrop={handleDrop}
          onDragStart={onDragStart}
          disabled={false}
          overlay={<ChartHeaderDragOverlay header={draggedHeader} />}
          items={headers}
        >
          <MultiGridTable
            ref={multiGridRef}
            columnCount={headers.length}
            rowCount={filteredAndSortedData.length}
            cellRenderer={cellRenderer}
            rowHeight={ROW_HEIGHT}
            columnWidthRenderer={getColumnWidth}
            onResize={onResize}
            fixedColumnCount={fixedColumnCount}
          />
        </DndSortable>
      </Container>
    </>
  );
};

export default ChartAnalysisTable;
